import { useMemo } from 'react';
import { Panel } from '@/components/Panel';
import { useOverview } from '@/hooks/useOverview';
import { Spinner, EmptyState, StatusBadge } from '@00start/ui';
import { formatDuration } from '@/utils/chart-helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Service {
  name: string;
  status: string;
  latencyMs?: number;
}

type Plane = 'organ' | 'bridge' | 'peer';

interface PlaneGroup {
  plane: Plane;
  label: string;
  services: Service[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cleanName(raw: string): string {
  return raw.replace(/riskscan-|-production|-staging/g, '');
}

function classifyPlane(name: string): Plane {
  const n = name.toLowerCase();
  if (/gateway|auth|bacchus/.test(n)) return 'organ';
  if (/config|health|incident/.test(n)) return 'bridge';
  return 'peer';
}

function statusColor(status: string): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'healthy' || s === 'up' || s === 'ok') return '#16A34A';
  if (s === 'degraded' || s === 'warning') return '#D97706';
  return '#DC2626';
}

function statusColorKey(status: string): 'resolved' | 'pending' | 'action_req' {
  const s = (status ?? '').toLowerCase();
  if (s === 'healthy' || s === 'up' || s === 'ok') return 'resolved';
  if (s === 'degraded' || s === 'warning') return 'pending';
  return 'action_req';
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const NODE_W = 120;
const NODE_H = 44;
const NODE_RX = 8;
const H_GAP = 20;  // horizontal gap between nodes in same plane
const V_GAP = 56;  // vertical gap between planes
const PAD_X = 24;
const PAD_Y = 24;
const PLANE_LABEL_H = 18; // height reserved above first node row for plane label

// ---------------------------------------------------------------------------
// Layout computation
// ---------------------------------------------------------------------------

interface NodeLayout {
  service: Service;
  x: number;
  y: number;
  plane: Plane;
}

interface PlaneLayout {
  plane: Plane;
  label: string;
  centerX: number;
  centerY: number; // center of node row
  nodes: NodeLayout[];
}

interface TopologyLayout {
  viewBox: string;
  planes: PlaneLayout[];
  edges: Array<{ x1: number; y1: number; x2: number; y2: number }>;
}

const PLANE_ORDER: Plane[] = ['organ', 'bridge', 'peer'];
const PLANE_LABELS: Record<Plane, string> = {
  organ: 'Organ',
  bridge: 'Bridge',
  peer: 'Peer',
};

function computeLayout(groups: PlaneGroup[]): TopologyLayout {
  const orderedGroups = PLANE_ORDER.map((p) => groups.find((g) => g.plane === p)).filter(
    Boolean,
  ) as PlaneGroup[];

  // Max nodes in any plane — used to compute viewBox width
  const maxNodes = Math.max(...orderedGroups.map((g) => g.services.length), 1);
  const svgW = PAD_X * 2 + maxNodes * NODE_W + (maxNodes - 1) * H_GAP;
  const svgH =
    PAD_Y * 2 +
    orderedGroups.length * (PLANE_LABEL_H + NODE_H) +
    (orderedGroups.length - 1) * V_GAP;

  const planes: PlaneLayout[] = [];

  orderedGroups.forEach((group, planeIdx) => {
    const rowY =
      PAD_Y + planeIdx * (PLANE_LABEL_H + NODE_H + V_GAP) + PLANE_LABEL_H;
    const count = group.services.length;
    const rowW = count * NODE_W + (count - 1) * H_GAP;
    const startX = (svgW - rowW) / 2;
    const nodeCenterY = rowY + NODE_H / 2;

    const nodes: NodeLayout[] = group.services.map((svc, i) => ({
      service: svc,
      x: startX + i * (NODE_W + H_GAP),
      y: rowY,
      plane: group.plane,
    }));

    planes.push({
      plane: group.plane,
      label: PLANE_LABELS[group.plane],
      centerX: svgW / 2,
      centerY: nodeCenterY,
      nodes,
    });
  });

  // Edges: connect each plane's center to the next plane's center (simple straight lines)
  const edges: TopologyLayout['edges'] = [];
  for (let i = 0; i < planes.length - 1; i++) {
    edges.push({
      x1: planes[i].centerX,
      y1: planes[i].centerY + NODE_H / 2,
      x2: planes[i + 1].centerX,
      y2: planes[i + 1].centerY - NODE_H / 2,
    });
  }

  return {
    viewBox: `0 0 ${svgW} ${svgH}`,
    planes,
    edges,
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SvgTopology({ layout }: { layout: TopologyLayout }) {
  return (
    <svg
      viewBox={layout.viewBox}
      style={{ width: '100%', minWidth: 320 }}
      aria-label="Service topology diagram"
    >
      {/* Edges */}
      {layout.edges.map((e, i) => (
        <line
          key={i}
          x1={e.x1}
          y1={e.y1}
          x2={e.x2}
          y2={e.y2}
          stroke="rgba(148,163,184,0.25)"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
      ))}

      {/* Planes */}
      {layout.planes.map((plane) => {
        const labelY = plane.centerY - NODE_H / 2 - 6;
        return (
          <g key={plane.plane}>
            {/* Plane label */}
            <text
              x={plane.centerX}
              y={labelY}
              textAnchor="middle"
              fontSize={10}
              fill="rgba(148,163,184,0.55)"
              fontFamily="SF Mono, monospace"
              letterSpacing={1}
            >
              {plane.label.toUpperCase()}
            </text>

            {/* Service nodes */}
            {plane.nodes.map((node) => {
              const color = statusColor(node.service.status);
              const displayName = cleanName(node.service.name);
              const latency = node.service.latencyMs;

              return (
                <g key={node.service.name}>
                  <rect
                    x={node.x}
                    y={node.y}
                    width={NODE_W}
                    height={NODE_H}
                    rx={NODE_RX}
                    ry={NODE_RX}
                    fill={color}
                    fillOpacity={0.15}
                    stroke={color}
                    strokeWidth={1.5}
                    strokeOpacity={0.8}
                  />
                  <text
                    x={node.x + NODE_W / 2}
                    y={node.y + (latency !== undefined ? NODE_H / 2 - 5 : NODE_H / 2 + 4)}
                    textAnchor="middle"
                    fontSize={11}
                    fill="rgba(226,232,240,0.9)"
                    fontFamily="SF Mono, monospace"
                  >
                    {displayName}
                  </text>
                  {latency !== undefined && (
                    <text
                      x={node.x + NODE_W / 2}
                      y={node.y + NODE_H / 2 + 10}
                      textAnchor="middle"
                      fontSize={9}
                      fill={color}
                      fillOpacity={0.85}
                      fontFamily="SF Mono, monospace"
                    >
                      {formatDuration(latency)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

function ListTopology({ groups }: { groups: PlaneGroup[] }) {
  const orderedGroups = PLANE_ORDER.map((p) => groups.find((g) => g.plane === p)).filter(
    Boolean,
  ) as PlaneGroup[];

  return (
    <div className="space-y-4">
      {orderedGroups.map((group) => (
        <div key={group.plane}>
          <p
            className="text-[10px] text-slate-500 tracking-widest mb-2"
            style={{ fontFamily: 'SF Mono, monospace' }}
          >
            {group.label.toUpperCase()}
          </p>
          <div className="space-y-1.5">
            {group.services.map((svc) => (
              <div
                key={svc.name}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-800/40"
              >
                <StatusBadge label={svc.status || 'unknown'} colorKey={statusColorKey(svc.status)} />
                <span
                  className="text-xs text-slate-300 flex-1 truncate"
                  style={{ fontFamily: 'SF Mono, monospace' }}
                >
                  {cleanName(svc.name)}
                </span>
                {svc.latencyMs !== undefined && (
                  <span
                    className="text-[10px] text-slate-500 shrink-0"
                    style={{ fontFamily: 'SF Mono, monospace' }}
                  >
                    {formatDuration(svc.latencyMs)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function ServiceTopology() {
  const { data, loading } = useOverview();

  const groups = useMemo<PlaneGroup[]>(() => {
    const services: Service[] = data?.health?.orgs?.flatMap((org) => org.services ?? []) ?? [];
    const map: Record<Plane, Service[]> = { organ: [], bridge: [], peer: [] };
    for (const svc of services) {
      map[classifyPlane(svc.name)].push(svc);
    }
    return PLANE_ORDER.filter((p) => map[p].length > 0).map((p) => ({
      plane: p,
      label: PLANE_LABELS[p],
      services: map[p],
    }));
  }, [data]);

  const layout = useMemo(() => computeLayout(groups), [groups]);

  if (loading) {
    return (
      <Panel title="Service Topology" wide mobilePriority={10}>
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      </Panel>
    );
  }

  if (groups.length === 0) {
    return (
      <Panel title="Service Topology" wide mobilePriority={10}>
        <EmptyState title="No services" description="No service data available" />
      </Panel>
    );
  }

  return (
    <Panel title="Service Topology" wide mobilePriority={10}>
      {/* Desktop / tablet: SVG topology */}
      <div
        className="hidden md:block"
        style={{ overflow: 'auto', touchAction: 'pinch-zoom' }}
      >
        <SvgTopology layout={layout} />
      </div>

      {/* Phone: collapsed list grouped by plane */}
      <div className="md:hidden">
        <ListTopology groups={groups} />
      </div>
    </Panel>
  );
}
